import moment from 'moment';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of } from 'rxjs';
import { Subscription } from 'rxjs/Subscription';
import { catchError, map, tap } from 'rxjs/operators';

import { ExtendedHttpResponse, HttpInterceptorService } from '../../core/services/http-interceptor.service';
import { ILogoutArgument } from '@ispm/user/interfaces/logout-argument.interface';
import { IUserLogin } from '@ispm/migration/interfaces/user-login.interface';
import { SessionRefreshService } from '@ispm/core/services/session-refresh.service';
import { TestSummaryActionsService } from '@ispm/dashboard/test/services/test-summary-actions.service';
import { UICustomizationService } from '@ispm/shared/services/ui-customization.service';
import { UsersStoreHandler } from '@ispm/dashboard/test/services/users-store-handler';
import { WebMessagingService } from '@ispm/core/services/web-messaging.service';
import { isEmpty } from 'lodash';

@Injectable()
export class AuthenticationService {
  readonly baseUrl = '/auth';

  public user: any;
  public doLogout$ = new BehaviorSubject<any>(false);

  private userInfo = { user: { }, orgInfo: { }, role: { }, addl: { }, info: { } };

  constructor(
    private _http: HttpInterceptorService,
    private usersStoreHandler: UsersStoreHandler,
    private testSummaryActionsService: TestSummaryActionsService,
    private sessionRefreshService: SessionRefreshService,
    private uiCustomizationService: UICustomizationService,
    private location: Location,
    private webMessagingService: WebMessagingService,
  ) {
  }

  public login(user: IUserLogin): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/login', { user })
      .pipe(
        map((res: ExtendedHttpResponse<any>) => {
          const userDetails = res.body;
          userDetails.localSettings = { };
          userDetails.isAdmin = this.isAdminUser(userDetails);
          userDetails.originalOrganization = userDetails.organization; // preserve the original organization, so it would be possible later on to reconstruct it if needed

          this.user = userDetails;
          localStorage.setItem('user', JSON.stringify(userDetails));
          sessionStorage.setItem('isLoggedInTab', 'true');

          this.sessionRefreshService.isNewSession = true;
          this.usersStoreHandler.setCurrentUser(userDetails);
          this.setCachedData();

          return res;
        }),
      );
  }

  public isSysAdminUser(user = null): boolean {
    const currUser = (user != null) ? user : this.getUser();

    return !!currUser?.permissions?.includes('ADMIN.ORGANIZATION.ACCESS.TO.ALL');
  }


  public isAdminUser(user = null): boolean {
    const currUser = (user != null) ? user : this.getUser();

    return !!currUser?.permissions?.includes('ADMIN.PORTAL.ACCESS');
  }

  private setCachedData(): void {
    if (!this.user) {
      return;
    }

    this.usersStoreHandler.setCurrentUser(this.user);
    let state = localStorage.getItem(`uc-${this.user.info.id}`);
    state = state ? JSON.parse(state) : null;
    this.testSummaryActionsService.setState(state, this.user.info.id);
  }

  public getUser(): any {
    if (!this.user) {
      this.user = JSON.parse(localStorage.getItem('user'));
      this.setCachedData();
    }
    return this.user;
  }

  public samlLogin(assertion: any): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/saml/login', { assertion })
      .pipe(
        map((res: ExtendedHttpResponse<any>) => {
          this.updateUserAndStore(res.body);

          return res;
        })
      );
  }

  public smartLogin(jwe: any): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/smart/launch', { jwe })
      .pipe(
        map((res: ExtendedHttpResponse<any>) => {
          this.updateUserAndStore(res.body.userData);

          return res;
        },
        catchError(error => {
          console.error('smart error ' + error);
          throw error;
        }))
      );
  }

  public oauth2Login(code: any, state: any): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/oauth2/getAccessTokenByAuthorizationCode', { code, state})
      .pipe(
        map((res: ExtendedHttpResponse<any>) => {
          this.updateUserAndStore(res.body.userData);

          return res;
        })
      );
  }

  private updateUserAndStore(user: any): void {
    user['loginViaSaml'] = true;
    user['localSettings'] = { };
    user['isAdmin'] = this.isAdminUser(user);
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));
    this.setCurrentUser(this.user);
    window.top.postMessage(user, '*');
    this.usersStoreHandler.setCurrentUser(user);
    this.setCachedData();
    this.sessionRefreshService.setIsSSOLogin();
    this.sessionRefreshService.isNewSession = true;
    sessionStorage.setItem('isLoggedInTab', 'true');
    const currUser = this.user;
    currUser['loginTime'] = moment.now();
    this.userInfo.user = { loginId: currUser.username };
    this.userInfo.orgInfo = currUser.organization;
    this.userInfo.role = currUser.role;
    this.userInfo.info = currUser.info;
    this.userInfo.addl = { name: currUser.managingOrganization, id: currUser.organization };
    this.audit(this.userInfo);
  }

  public audit(user: any): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/audit', user);
  }

  public checkSession(): any {
    return new Promise((resolve, reject) => {
      if (!this.getUser()) {
        reject();
      } else {
        this._http.get(this.baseUrl + '/status')
          .subscribe(
            (data: ExtendedHttpResponse<any>) => {
              sessionStorage.setItem('isLoggedInTab', 'true');
              resolve(data);
            },
            () => reject()
          );
      }
    });
  }

  public refresh(params?: any): void {
    const fromClient = params ? !!params.fromClient : false;

    this._http.post(this.baseUrl + '/refresh?fromClient=' + fromClient, { })
      .subscribe(
        () => { },
        () => { }
      );
  }

  public logout(isToRefresh: ILogoutArgument): Subscription {
    this.webMessagingService.stopService();
    localStorage?.removeItem('parentNodesState'); // removing the node information of the admin
    localStorage?.removeItem('globalNodesState');
    sessionStorage.setItem('duplicatePatientWarning', '[]'); // removing the duplicate patient warning information
    sessionStorage.removeItem('currentApplication');
    sessionStorage.removeItem('adminSelectOrganizationId');
    sessionStorage.removeItem('Orgnization');
    sessionStorage.removeItem('tableState');
    this.uiCustomizationService.cleanUICustomResource();

    if (isToRefresh.refresh) {
      const user = this.getUser();

      if (this.user) {
        const isAdmin = this.isAdminUser(user);

        return this._http.post(this.baseUrl + '/logout', user)
          .pipe(
            tap(() => this.doLogout$.next(true))
          )
          .subscribe(
            () => { },
            (error): any => {
              console.error(error);
              this.location.go('/login');
              window.location.reload();
            },
            () => {
              localStorage.removeItem(this.sessionRefreshService.lastUserInteractionKey);
              this.setCurrentUser(null);

              if (isToRefresh.skipNavigationToLogin && user.loginViaSaml) {
                this.location.go('/generic-error?errorType=LoginFailed');
              } else {
                if (isToRefresh.callFromResponseHandler) {
                  sessionStorage.setItem('ShowLoggedWarning', 'true');
                }

                if (isAdmin) {
                  this.redirectToClientLoginPage();
                } else {
                  this.location.go('/login');
                  window.location.reload();
                }
              }
            }
          );
      } else {
        this.location.go('/login');
        window.location.reload();
      }
    }
    else {
      this.setCurrentUser(null);
      this.location.go('/login');
    }
  }

  public setCurrentUser(user: any, isSamlLogin = false): any {
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));
    this.usersStoreHandler.setCurrentUser(user);

    if (isSamlLogin) {
      this.sessionRefreshService.setIsSSOLogin();
      }
    return this.user;
  }

  private redirectToClientLoginPage(): void {
    const webEndPoint = localStorage.getItem('webEndPoint');

    if (webEndPoint) {
      this.logoutWithCustomRedirection(webEndPoint);
    }
  }

  private logoutWithCustomRedirection(webEndPoint: string): void {
    this.setCurrentUser(null);
    window.open(webEndPoint + '/login?clearCacheUser=1', '_self');
  }

  public info(): Observable<ExtendedHttpResponse<any>> {
    return this._http.get(this.baseUrl);
  }

  public cleanUICustomResource():void {
    this.uiCustomizationService.cleanUICustomResource();
  };

  public CheckExternalLogin(): Observable<any> {
    const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');

    // If cached data exists, return it directly
    if (!isEmpty(cachedUser)) {
      return of(cachedUser);
    }
    return this._http.get(this.baseUrl + '/getUserData')
      .pipe(
        map((res: ExtendedHttpResponse<any>) => {
          const user = res.body;
          user.localSettings = { };
          this.user = user;
          user.isAdmin = this.isAdminUser(user);
          user.originalOrganization = user.organization; // preserve the original organization, so it would be possible later on to reconstruct it if needed
          localStorage.setItem('user', JSON.stringify(user));
          this.usersStoreHandler.setCurrentUser(user);

          return user;
        })
      );
  }

  public authorize(state: any): Observable<ExtendedHttpResponse<any>> {
    return this._http.post(this.baseUrl + '/authorize', state);
  }

  public updateLocalSettings(propertyName: string, value: any): void {
    this.user.localSettings[propertyName] = value;
    this.setCurrentUser(this.user);
  };

  public getLocalSetting(propertyName: string, defaultValue: any): any {
    return this.user.localSettings[propertyName] !== undefined ? this.user.localSettings[propertyName] : defaultValue;
  }

  public doesUserHasPermission(permission: string): boolean {
    if (this.getUser() && this.getUser().permissions) {
      return this.getUser().permissions.includes(permission);
    }

    return false;
  }

  public doesUserHaveRegExpPermission(regexp: any): boolean {
    if (regexp.length === 0) {
      return false;
    }

    // Make sure regexp covers entire permission
    if (regexp[0] !== '^') {
      regexp = '^' + regexp;
    }

    if (regexp[regexp.length - 1] !== '$') {
      regexp += '$';
    }

    if (this.getUser() && this.getUser().permissions) {
      const pattern = new RegExp(regexp);

      return this.getUser().permissions.find((permission: any) => pattern.test(permission));
    }

    return false;
  }

  /**
  * revert the user's organization to the preserved original organization value.
  * this method is used whenever the user's organization is being set according to the current selected organization
  *  and wishing to reconstruct the original organization value
  */
  public revertUserOriginalOrganization(): void {
    this.getUser().organization = this.getUser().originalOrganization;
  }
}
